import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Radio, Loader2, MicVocal, Trophy } from "lucide-react";
import { API_BASE } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function RequestPublic() {
  const { stationId } = useParams();
  const [station, setStation] = useState(null);
  const [board, setBoard] = useState(null);
  const mountRef = useRef(null);
  const injected = useRef(false);

  useEffect(() => {
    axios.get(`${API_BASE}/request-line/public/${stationId}/meta`)
      .then((r) => setStation(r.data))
      .catch(() => setStation(false));
    axios.get(`${API_BASE}/request-line/public/${stationId}/leaderboard`)
      .then((r) => setBoard(r.data))
      .catch(() => {});
  }, [stationId]);

  useEffect(() => {
    if (injected.current || !mountRef.current || station === false || !station) return;
    injected.current = true;
    const s = document.createElement("script");
    s.src = `${BACKEND}/api/embed/requestline.js`;
    s.setAttribute("data-station", stationId);
    mountRef.current.appendChild(s);
  }, [stationId, station]);

  if (station === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#08080c]" data-testid="request-public-notfound">
        <div className="text-center px-6">
          <MicVocal className="h-10 w-10 mx-auto text-white/20 mb-4" />
          <div className="font-display text-2xl font-bold text-white mb-2">Station not found</div>
          <div className="text-sm text-white/40">Double-check the link with the station.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#08080c]" data-testid="request-public-page">
      <header className="px-5 pt-8 pb-5 text-center">
        <div className="inline-flex items-center gap-2 mb-2">
          <Radio className="h-4 w-4 text-[#f5a623]" />
          <span className="font-display font-bold text-white text-lg" data-testid="request-public-station">
            {station ? station.station_name : <Loader2 className="h-4 w-4 animate-spin inline" />}
          </span>
        </div>
        <div className="text-xs text-white/40">
          Request a song — and record a shoutout in your own voice to hear it on air.
        </div>
      </header>

      <main className="flex-1 flex flex-col items-center px-4 pb-8">
        <div ref={mountRef} data-testid="request-public-widget" />

        {board && (
          <div className="w-full max-w-[420px] mt-5 rounded-2xl border border-white/10 bg-white/[0.03] p-5" data-testid="request-public-leaderboard">
            <div className="flex items-center gap-2 mb-3">
              <Trophy className="h-3.5 w-3.5 text-[#f5a623]" />
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">Most requested this week</span>
            </div>
            {board.eligible ? (
              <div className="space-y-2" data-testid="leaderboard-items">
                {board.items.map((it) => (
                  <div key={it.rank} className="flex items-center gap-3" data-testid={`leaderboard-row-${it.rank}`}>
                    <span className={`w-6 text-center font-display font-bold ${it.rank === 1 ? "text-[#f5a623] text-lg" : "text-white/30 text-sm"}`}>{it.rank}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold text-white truncate">{it.song_title}</div>
                      <div className="text-[11px] text-white/40 truncate">{it.artist}</div>
                    </div>
                    <span className="text-[10.5px] font-mono text-white/40 shrink-0">{it.count} request{it.count === 1 ? "" : "s"}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-[12px] text-white/50" data-testid="leaderboard-empty">
                The chart is wide open — <span className="text-[#f5a623] font-semibold">be the first to set this week's #1</span>. Request your song above!
              </div>
            )}
          </div>
        )}
      </main>

      <footer className="px-5 py-4 text-center">
        <a href="/?utm_source=request_page" data-testid="request-public-footer"
          className="text-[11px] font-mono uppercase tracking-widest text-white/25 hover:text-[#f5a623] transition-colors">
          Powered by BroadcastFlow AI
        </a>
      </footer>
    </div>
  );
}
